/**
 * Module dependencies.
 */

import { Button } from 'src/components/core/buttons/button';
import { Control, UseFormGetValues, useWatch } from 'react-hook-form';
import { ReactNode, useCallback, useState } from 'react';
import { media } from 'src/styles/utils/media';
import { useTranslation } from 'next-i18next';
import { validatePromoCode } from 'src/requests/promo-code';
import iconButton from 'src/assets/svg/button.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  control: Control<{ promoCode: string }>;
  onClick?: () => Promise<void>;
  previousPromoCode: string | undefined;
};

/**
 * Export `promoCodeFieldName` constant.
 */

export const promoCodeFieldName = 'promoCode';

/**
 * `UseValidatePromoCodeOptions` type.
 */

type UseValidatePromoCodeOptions = {
  getValues: UseFormGetValues<any>;
  onError: (code: string) => void;
  onSuccess: (code: string) => void;
};

/**
 * `UseValidatePromoCode` type.
 */

type UseValidatePromoCode = [Props['previousPromoCode'], Props['onClick']];

/**
 * `PromoCodeWrapper` styled component.
 */

const PromoCodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px 0;

  ${media.min.ms`
    align-items: flex-start;
    grid-gap: 0 32px;
    display: grid;
    grid-template-columns: 1fr 1fr;
  `}

  ${media.min.md`
    grid-gap: 32px 0;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, max-content);
  `}

  ${media.min.lg`
    grid-gap: 0 32px;
    grid-template-columns: 1fr 1fr;
  `}

  & > :last-child {
    margin-bottom: 0;
  }
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: max-content;

  ${media.min.ms`
    margin-top: 25px;
  `}

  ${media.min.md`
    margin-top: 0;
  `}

  ${media.min.lg`
    margin-top: 25px;
  `}
`;

/**
 * `useValidatePromoCode` hook.
 */

export function useValidatePromoCode(
  options: UseValidatePromoCodeOptions
): UseValidatePromoCode {
  const { getValues, onError, onSuccess } = options;
  const [previousPromoCode, setPreviousPromoCode] = useState<
    string | undefined
  >();
  const validate = useCallback(async () => {
    const code = getValues(promoCodeFieldName);
    const responseSuccess = await validatePromoCode(code as string);
    const callback = responseSuccess ? onSuccess : onError;

    setPreviousPromoCode(code);
    callback(code);
  }, [getValues, onError, onSuccess]);

  return [previousPromoCode, validate];
}

/**
 * Export `PromoCodeFieldWrapper` component.
 */

export function PromoCodeFieldWrapper({
  children,
  control,
  onClick,
  previousPromoCode
}: Props) {
  const { t } = useTranslation('common');
  const promoCode = useWatch({ control, name: promoCodeFieldName });

  return (
    <PromoCodeWrapper>
      {children}

      <StyledButton
        colorTheme={'greenOutlined'}
        disabled={!promoCode || previousPromoCode === promoCode}
        icon={iconButton}
        onClick={onClick}
      >
        {t('actions.validateCode')}
      </StyledButton>
    </PromoCodeWrapper>
  );
}
